import { useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { cn } from "@/lib/utils";
import {
  useMyPackaging,
  useDeletePackaging,
  useSetDefaultPackaging,
  type CustomerPackaging,
} from "@/api/use-packaging-api";
import { PackagingDialog } from "./packaging-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Lightbulb,
  Package,
  Plus,
  Pencil,
  Trash2,
  Star,
  MoreVertical,
} from "lucide-react";
import { PageHeader } from "@/components/page-chrome";
import { useTranslation } from "react-i18next";

function BoxIllustration({
  length,
  width,
  height,
}: {
  length: number;
  width: number;
  height: number;
}) {
  const maxDim = Math.max(length, width, height);
  const scale = 60 / maxDim;
  const W = width * scale;
  const H = height * scale;
  const D = length * scale * 0.5;
  return (
    <svg
      width="120"
      height="100"
      viewBox="0 0 120 100"
      style={{ overflow: "visible" }}
      aria-hidden
    >
      <polygon
        points={`${60 - W / 2},${50 - H / 2} ${60 - W / 2 + D},${50 - H / 2 - D} ${60 + W / 2 + D},${50 - H / 2 - D} ${60 + W / 2},${50 - H / 2}`}
        fill="hsl(var(--heroui-primary-100))"
        stroke="hsl(var(--heroui-primary))"
        strokeWidth={1.4}
      />
      <rect
        x={60 - W / 2}
        y={50 - H / 2}
        width={W}
        height={H}
        fill="hsl(var(--heroui-primary-50))"
        stroke="hsl(var(--heroui-primary))"
        strokeWidth={1.4}
      />
      <polygon
        points={`${60 + W / 2},${50 - H / 2} ${60 + W / 2 + D},${50 - H / 2 - D} ${60 + W / 2 + D},${50 + H / 2 - D} ${60 + W / 2},${50 + H / 2}`}
        fill="hsl(var(--heroui-primary-200))"
        stroke="hsl(var(--heroui-primary))"
        strokeWidth={1.4}
      />
      <line
        x1={60 - W / 2}
        y1={50}
        x2={60 + W / 2}
        y2={50}
        stroke="hsl(var(--heroui-primary))"
        strokeWidth={1}
        strokeDasharray="2 2"
        opacity={0.5}
      />
    </svg>
  );
}

export default function PackagingScreen() {
  const { t } = useTranslation("app");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackaging, setEditingPackaging] =
    useState<CustomerPackaging | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { toast } = useToast();
  const { data, isLoading } = useMyPackaging();
  const packaging = data?.boxes ?? [];
  const maxPackaging = data?.maxBoxes ?? 5;

  const deletePackagingMutation = useDeletePackaging();
  const setDefaultMutation = useSetDefaultPackaging();

  const handleEdit = (pkg: CustomerPackaging) => {
    setEditingPackaging(pkg);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingPackaging(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await deletePackagingMutation.mutateAsync(deletingId);
      toast({ title: t("packaging.deletedToast") });
    } catch {
      toast({ variant: "destructive", title: t("packaging.deleteErrorToast") });
    }
    setDeletingId(null);
  };

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultMutation.mutateAsync(id);
      toast({ title: t("packaging.defaultUpdatedToast") });
    } catch {
      toast({
        variant: "destructive",
        title: t("packaging.defaultErrorToast"),
      });
    }
  };

  return (
    <div className="space-y-9">
      <PageHeader
        eyebrow={t("packaging.eyebrow")}
        title={t("packaging.title")}
        italic={t("packaging.italic")}
        lede={t("packaging.lede")}
        right={
          <Button
            color="primary"
            onPress={handleAdd}
            isDisabled={packaging.length >= maxPackaging}
            startContent={<Plus className="h-4 w-4" />}
          >
            {t("packaging.addBox", {
              count: packaging.length,
              max: maxPackaging,
            })}
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {packaging.map((pkg: CustomerPackaging) => (
            <div
              key={pkg.id}
              className={cn(
                "bg-content1 rounded-large p-5 flex flex-col gap-3 shadow-small",
                pkg.isDefault && "ring-2 ring-inset ring-primary shadow-medium"
              )}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="text-base font-semibold tracking-tight">
                      {pkg.name}
                    </h3>
                    {pkg.isDefault && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-tiny font-semibold">
                        <Star className="h-2.5 w-2.5 fill-current" />{" "}
                        {t("packaging.default")}
                      </span>
                    )}
                  </div>
                </div>
                <Dropdown placement="bottom-end">
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      size="sm"
                      aria-label={t("packaging.actions")}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label={t("packaging.actions")}>
                    <DropdownItem
                      key="edit"
                      startContent={<Pencil className="h-4 w-4" />}
                      onPress={() => handleEdit(pkg)}
                    >
                      {t("packaging.edit")}
                    </DropdownItem>
                    {!pkg.isDefault ? (
                      <DropdownItem
                        key="set-default"
                        startContent={<Star className="h-4 w-4" />}
                        isDisabled={setDefaultMutation.isPending}
                        onPress={() => handleSetDefault(pkg.id)}
                      >
                        {t("packaging.setAsDefault")}
                      </DropdownItem>
                    ) : null}
                    <DropdownItem
                      key="delete"
                      className="text-danger"
                      color="danger"
                      startContent={<Trash2 className="h-4 w-4" />}
                      onPress={() => setDeletingId(pkg.id)}
                    >
                      {t("packaging.delete")}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>

              <div className="h-[120px] rounded-medium bg-gradient-to-b from-default-50 to-default-100 grid place-items-center">
                <BoxIllustration
                  length={pkg.length}
                  width={pkg.width}
                  height={pkg.height}
                />
              </div>

              <div className="grid grid-cols-3 gap-2 text-sm">
                {(
                  [
                    { l: "L", v: pkg.length },
                    { l: "W", v: pkg.width },
                    { l: "H", v: pkg.height },
                  ] as const
                ).map((d) => (
                  <div
                    key={d.l}
                    className="px-2.5 py-2 rounded-medium bg-default-50 flex items-baseline justify-between"
                  >
                    <span className="text-tiny font-semibold tracking-wider text-default-500">
                      {d.l}
                    </span>
                    <span className="font-mono font-semibold">
                      {d.v}
                      <span className="text-tiny text-default-400 ml-0.5">
                        {pkg.dimensionUnit}
                      </span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2 border-t border-divider">
                <Button
                  variant="bordered"
                  size="sm"
                  startContent={<Pencil className="h-3.5 w-3.5" />}
                  onPress={() => handleEdit(pkg)}
                >
                  {t("packaging.edit")}
                </Button>
                {!pkg.isDefault && (
                  <Button
                    variant="light"
                    size="sm"
                    startContent={<Star className="h-3.5 w-3.5" />}
                    isDisabled={setDefaultMutation.isPending}
                    onPress={() => handleSetDefault(pkg.id)}
                  >
                    {t("packaging.setDefault")}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {packaging.length < maxPackaging && (
            <button
              onClick={handleAdd}
              className="rounded-large border-2 border-dashed border-default-300 hover:border-primary hover:bg-primary-50/30 transition-colors p-8 min-h-[280px] flex flex-col items-center justify-center gap-3 text-center cursor-pointer"
            >
              <div className="w-14 h-14 rounded-full bg-default-100 grid place-items-center text-default-500">
                <Plus className="h-6 w-6" />
              </div>
              <div className="text-sm font-semibold text-default-700">
                {t("packaging.addAnother")}
              </div>
              <div className="text-xs text-default-500 max-w-[200px]">
                {t("packaging.slotsAvailable", {
                  count: maxPackaging - packaging.length,
                })}
              </div>
            </button>
          )}

          {packaging.length === 0 && (
            <div className="col-span-full text-center text-default-500 py-4">
              <Package className="h-12 w-12 mx-auto text-default-400 mb-3" />
              <p>{t("packaging.empty")}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-4 items-start rounded-large bg-content1 border border-divider p-5">
        <div className="w-9 h-9 rounded-medium bg-primary-50 text-primary grid place-items-center flex-shrink-0">
          <Lightbulb className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold mb-0.5">
            {t("packaging.whyTitle")}
          </div>
          <div className="text-sm text-default-600 leading-normal">
            {t("packaging.whyBody")}
          </div>
        </div>
      </div>

      <PackagingDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setEditingPackaging(null);
        }}
        packaging={editingPackaging}
      />

      <Modal
        isOpen={!!deletingId}
        onOpenChange={() => setDeletingId(null)}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{t("packaging.deleteTitle")}</ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-500">
                  {t("packaging.deleteBody")}
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("common:cancel")}
                </Button>
                <Button
                  color="danger"
                  onPress={handleDelete}
                  isLoading={deletePackagingMutation.isPending}
                >
                  {t("packaging.delete")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
